import { useEffect, useState } from 'react'
import { api, money, fromPaise, ApiError } from '../api'
import { Page } from '../App'
import { Card, Alert, Spinner, Empty, Modal, Field } from '../components/ui'

interface Item {
  id: string
  name: string
  description: string | null
  sac_hsn: string | null
  unit: string
  rate: number
  currency: string
  tax_rate: number
  is_service: number
}

export default function Items() {
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Item | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      setItems((await api.get<{ items: Item[] }>('/items')).items)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  return (
    <Page
      title="Items & services"
      subtitle="Reusable line items so invoicing is three clicks, not three minutes"
      actions={
        <button className="btn primary" onClick={() => setCreating(true)}>
          + New item
        </button>
      }
    >
      <Alert kind="error">{error}</Alert>

      {items.some((i) => i.name.startsWith('PLACEHOLDER')) && (
        <Alert kind="warn">
          Some seeded items are still placeholders. Edit them with your real service names, SAC codes
          and rates — or archive them.
        </Alert>
      )}

      <Card tight>
        {loading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <Empty icon="≡" title="No items yet" />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>SAC/HSN</th>
                  <th>Unit</th>
                  <th className="r">Default rate</th>
                  <th className="r">GST</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{i.name}</div>
                      {i.description && <div className="muted small">{i.description}</div>}
                    </td>
                    <td className="mono small">{i.sac_hsn ?? '—'}</td>
                    <td className="small">{i.unit}</td>
                    <td className="num">{i.rate ? money(i.rate, i.currency) : <span className="muted">—</span>}</td>
                    <td className="num">{i.tax_rate}%</td>
                    <td className="right nowrap">
                      <button className="btn sm" onClick={() => setEditing(i)}>
                        Edit
                      </button>{' '}
                      <button
                        className="btn sm danger"
                        onClick={async () => {
                          await api.del(`/items/${i.id}`)
                          load()
                        }}
                      >
                        Archive
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(creating || editing) && (
        <ItemForm
          item={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            load()
          }}
        />
      )}
    </Page>
  )
}

function ItemForm({
  item,
  onClose,
  onSaved,
}: {
  item: Item | null
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(item?.name ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [sac, setSac] = useState(item?.sac_hsn ?? '')
  const [unit, setUnit] = useState(item?.unit ?? 'NOS')
  const [rate, setRate] = useState(item ? fromPaise(item.rate) : '')
  const [taxRate, setTaxRate] = useState(String(item?.tax_rate ?? 18))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setError('')
    setBusy(true)
    try {
      const body = {
        name,
        description: description || null,
        sac_hsn: sac || null,
        unit,
        rate: rate || 0,
        tax_rate: Number(taxRate),
      }
      if (item) await api.patch(`/items/${item.id}`, body)
      else await api.post('/items', body)
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={item ? 'Edit item' : 'New item'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>
      <Field label="Name">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="Description" hint="Used as the default invoice line description.">
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="grid c2">
        <Field label="SAC / HSN code" hint="Services use SAC, e.g. 998314 for software development.">
          <input type="text" className="mono" value={sac} onChange={(e) => setSac(e.target.value)} />
        </Field>
        <Field label="Unit">
          <input type="text" value={unit} onChange={(e) => setUnit(e.target.value.toUpperCase())} />
        </Field>
        <Field label="Default rate (₹)">
          <input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
        </Field>
        <Field label="GST rate (%)">
          <input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
